import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import Switch from "@mui/material/Switch";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import DeleteDialog from "../common/DeleteDialog";
import NewMailSetting from "./NewMailSetting";
import { AddBox } from "@mui/icons-material";
import ConfigData from "../config/config.json";
import { GlobalMessageContext } from "../app/App";
import Tooltip from "@mui/material/Tooltip";

const webserver = ConfigData.WEBSERVER;

const label = { inputProps: { "aria-label": "Switch demo" } };

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "mail",
    numeric: false,
    disablePadding: false,
    label: "E-Mail",
  },
  {
    id: "mailAll",
    numeric: false,
    disablePadding: false,
    label: "Alle Prüfungsergebnisse",
  },
  {
    id: "mailFailed",
    numeric: false,
    disablePadding: false,
    label: "Ausschließlich negative Prüfungsergebnisse",
  },
];

function EnhancedTableHead(props) {
  const [open, setOpen] = React.useState(false);
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align="right">
            <Tooltip title={"E-Mail hinzufügen"} enterDelay={1000}>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{ color: "#00aa00", float: "right" }}
              >
                <AddBox></AddBox>
              </IconButton>
            </Tooltip>
        </TableCell>
        <NewMailSetting
          open={open}
          setOpen={setOpen}
          setRows={props.setRows}
          rows={props.rows}
        />
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function EnhancedTable() {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("mail");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");

  const { setGlobalMessage } = React.useContext(GlobalMessageContext);

  const [rows, setRows] = useState([]);

  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/settingMail/getAll",
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        for (var i = 0; i < data.length; i++) {
          data[i].deleteOpen = false;
        }
        setRows(data);
      })
      .catch((error) => {});
  }, []);

  const setMailAll = (index, id, activity) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/settingMail/setMailAll/" +
        id +
        "?activity=" +
        activity,
      { method: "PUT", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then(() => {
        setRows((prev) => {
          let rowsCopy = JSON.parse(JSON.stringify(prev));
          rowsCopy[index].mailAll = activity;
          setGlobalMessage({
            globalMessage: "Änderung erfolgreich",
            severity: "success",
          });
          return rowsCopy;
        });
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  const setMailFailed = (index, id, activity) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/settingMail/setMailFailed/" +
        id +
        "?activity=" +
        activity,
      { method: "PUT", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then(() => {
        setRows((prev) => {
          let rowsCopy = JSON.parse(JSON.stringify(prev));
          rowsCopy[index].mailFailed = activity;
          setGlobalMessage({
            globalMessage: "Änderung erfolgreich",
            severity: "success",
          });
          return rowsCopy;
        });
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  const deleteMailSetting = (index, id) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/settingMail/delete/" +
        id,
      { method: "DELETE", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then(() => {
        setRows((prev) => {
          let rowsCopy = JSON.parse(JSON.stringify(prev));
          rowsCopy.splice(index, 1);
          setGlobalMessage({
            globalMessage: "E-Mail entfernt",
            severity: "success",
          });
          return rowsCopy;
        });
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  const filteredRows = rows.filter((row) => {
    return (
      row.mail.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.mailAll.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              setRows={setRows}
              rows={rows}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <TableCell align="left">{row.mail}</TableCell>
                      <TableCell align="left" padding="none">
                        <Switch
                          {...label}
                          checked={row.mailAll}
                          onChange={() =>
                            setMailAll(index, row.id, !row.mailAll)
                          }
                          sx={{ paddingLeft: 1.85 }}
                        />
                      </TableCell>
                      <TableCell align="left" padding="none">
                        <Switch
                          {...label}
                          checked={row.mailFailed}
                          onChange={() =>
                            setMailFailed(index, row.id, !row.mailFailed)
                          }
                          sx={{ paddingLeft: 1.85 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                          <Tooltip title={"E-Mail Einstellungen löschen"} enterDelay={1000}>
                            <IconButton
                              onClick={() =>
                                setRows((prev) => {
                                  let temp = JSON.parse(JSON.stringify(prev));
                                  temp[index].deleteOpen = true;
                                  return temp;
                                })
                              }
                            >
                              <UnsubscribeIcon sx={{ color: "#b50202" }} />
                            </IconButton>
                          </Tooltip>
                      </TableCell>
                      <DeleteDialog
                        function={() => deleteMailSetting(index, row.id)}
                        open={
                          row.deleteOpen !== undefined ? row.deleteOpen : false
                        }
                        setOpen={() =>
                          setRows((prev) => {
                            let temp = JSON.parse(JSON.stringify(prev));
                            temp[index].deleteOpen = false;
                            return temp;
                          })
                        }
                        title="Mail-Einstellungen löschen"
                        text={
                          'Sind Sie sicher, dass Sie die E-Mail-Einstellungen von "' +
                          row.mail +
                          '" unwiderruflich entfernen wollen?'
                        }
                      />
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => {
            return "" + from + "-" + to + " von " + count;
          }}
          labelRowsPerPage="Zeilen pro Seite"
        />
      </Paper>
    </Box>
  );
}
